import { getIncomers } from '@xyflow/react';
import { getDescriptionsFromImageNodes, getTextFromTextNodes, getAudiosFromAudioNodes, getAudiosFromUploadNodes, resolveNodeAssetRefUrls } from '@/lib/xyflow';
import { handleError } from '@/lib/error/handle';
import { speechModels } from '@flowmuse/models/speech';
import { buildProviderOptions } from '@flowmuse/models/speech/ui-options';
import { env } from '@/lib/env';
import { ensureProjectSavedOrShowRefreshModal } from '@/lib/project-save';
import { checkConcurrency } from '@/lib/check-concurrency';

type TranslateFn = (key: string, vars?: Record<string, unknown>) => string;

export type CreateAudioHandleGenerateParams = {
    t: TranslateFn;
    project: { id?: string } | null;
    id: string;
    getNodes: () => any[];
    getEdges: () => any[];
    data: any;
    modelId: string;
    updateNodeData: (id: string, partial: any) => void;
};

export const createAudioHandleGenerate = (params: CreateAudioHandleGenerateParams) => {
    const { t, project, id, getNodes, getEdges, data, modelId, updateNodeData } = params;

    const handleGenerate = async () => {
        try {
            if (!project?.id) {
                throw new Error('Project not found');
            }

            // ========================== 生成前强制保存并等待（严格） ==========================
            // 保存失败/超时：不继续生成，并提示用户刷新
            const ok = await ensureProjectSavedOrShowRefreshModal(10000);
            if (!ok) return;

            // 并发检查
            if (!await checkConcurrency(project.id!)) return;

            // 仅设置需要变更的字段，updateNodeData 会浅合并，避免展开陈旧闭包数据
            updateNodeData(id, {
                status: 'processing',
                updatedAt: new Date().toISOString(),
                error: undefined,
                runtimeEstimatedTime: undefined,
                // 清除上一次生成残留的追踪 id：否则轮询会立刻用「旧的、已完成的」generationId
                // 查到 completed + 旧结果，瞬间把节点标记完成并显示旧音频，而真正新发起的生成仍在后台运行。
                // 清空后本次生成先进入 nodeId 兜底模式（带 30s 门槛），待 202 写入新 generationId 后再正常轮询。
                processingGenerationId: undefined,
                processingTaskId: undefined,
            });

            const incomers = getIncomers({ id }, getNodes(), getEdges());
            const textPrompts = getTextFromTextNodes(incomers);
            const imagePrompts = getDescriptionsFromImageNodes(incomers);
            // 语音模型不支持 Asset:// 协议：上游音频若已提交审核（url 为 Asset:// 引用），解析回预览 URL
            const audios = resolveNodeAssetRefUrls(getAudiosFromAudioNodes(incomers), incomers);
            // 获取 incomers 中的上传节点（音频类型）, 兜底 []
            const audios_upload = resolveNodeAssetRefUrls(getAudiosFromUploadNodes(incomers), incomers);
            // 收集内连音频附件（节点内直接上传/从素材库选择的参考音频）, 兜底 []
            const inlineAtts = Array.isArray((data as any)?.inlineAttachments) ? (data as any).inlineAttachments as any[] : [];
            const inlineAudios = inlineAtts
                .filter((a: any) => a?.fileType === 'audio' && a?.url)
                .map((a: any) => ({ url: a.previewUrl ?? a.url, type: a.type || 'audio/mpeg' }));
            // 合并音频数组
            const allAudios = [...audios, ...audios_upload, ...inlineAudios];
            const modelMeta = speechModels[modelId]
            const precheck = modelMeta.precheck ?? []

            let text = textPrompts.join(';');
            let instructions = data?.instructions as string | undefined;

            const musicLyrics = (data?.modelUi as any)?.musicLyrics as string | undefined;
            const hasLyricsInput = Boolean(modelMeta?.voices?.includes('lyrics'));

            if (instructions && !text.length) {
                text = instructions;
                instructions = undefined;
            }

            const hideLyricsVoices = (modelMeta as any)?.hideLyricsVoices as string[] | undefined;
            const lyricsVisible = hasLyricsInput && !(hideLyricsVoices ?? []).includes((data as any)?.voice ?? '');

            for (const p of precheck) {
                if (p === 'prompt' && (!text.trim() && !instructions?.trim())) {
                    throw new Error('Please input prompt');
                }
                if (p === 'lyrics' && lyricsVisible && !musicLyrics?.trim()) {
                    throw new Error('Please input lyrics');
                }
                if (p === 'audio' && allAudios.length === 0) {
                    throw new Error('Please input audio');
                }
            }

            const durationSeconds = (data as any)?.durationSeconds as number | undefined;
            const modelUi = (data as any)?.modelUi ?? {};
            const builtOptions = buildProviderOptions(modelId, modelUi);
            if (hasLyricsInput && musicLyrics?.trim()) {
                builtOptions.lyrics = musicLyrics.trim();
            }
            const providerOptions = (allAudios.length || durationSeconds != null || Object.keys(builtOptions).length > 0
                ? {
                    url: allAudios.length ? { audio_input: allAudios.map(a => a.url) } : undefined,
                    meta: durationSeconds != null ? { durationSeconds } : undefined,
                    ...builtOptions,
                }
                : undefined) as any;

            // 走 API 队列
            const resp = await fetch('/api/speech/create', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'x-internal-secret': env.NEXT_PUBLIC_INTERNAL_API_SECRET },
                body: JSON.stringify({
                    projectId: project.id,
                    nodeId: id,
                    modelId,
                    text,
                    instructions,
                    voice: (data as any)?.voice,
                    providerOptions,
                }),
                signal: AbortSignal.timeout(50_000),
            });

            const json = (await resp.json()) as { queued?: boolean; generationId?: string; error?: string; estimatedTime?: number };
            if (!resp.ok || json.error) {
                throw new Error(json.error || 'Server Request failed');
            }
            if (resp.status === 202) {
                if (!json.generationId) throw new Error('Generation ID not found');
                updateNodeData(id, { processingGenerationId: json.generationId, runtimeEstimatedTime: json.estimatedTime });
                return;
            }
        } catch (error) {
            try {
                updateNodeData(id, {
                    status: 'failed',
                    updatedAt: new Date().toISOString(),
                    error: error instanceof Error ? error.message : undefined,
                    processingGenerationId: undefined,
                    runtimeEstimatedTime: undefined,
                });
            } catch {}
            throw error;
        }
    };

    return handleGenerate;
};
